import { Store } from "redux";
import jwt_decode from "jwt-decode";
import { get } from "lodash";
import { fromUnixTime, isAfter } from "date-fns";
import { PRODUCTS_FDR } from "@tvg/conf/src/product";
import tvgConf from "@tvg/conf";

import { amplitudeRemovedGeoToken } from "@urp/amplitude/src/modules/geolocation/utils";
import { addCookie, getCookieValue, removeCookie } from "./cookieUtils";

interface AuthToken {
  ses: number;
  sub: number;
  usn: string;
  prd: string;
  crt: number;
  eml: string;
  src: number;
  mfa: boolean;
  typ: number;
  exp: number;
}
// como divido este? um com as coisas genericas, outro AUTH_TOKEN_COOKIE, LOGIN_TOKEN_COOKIE e WRAPPER_CONFIG_COOKIE,e outro com o resto
export const AUTH_TOKEN_COOKIE = "X-Auth-Token";
export const LOGIN_TOKEN_COOKIE = "X-Login-Token";
export const GEO_TOKEN_COOKIE = "X-Geo-Token";
export const GEO_LICENSE_COOKIE = "X-Geo-License";
export const SPORTSBOOK_REGION_COOKIE = "X-Sportsbook-Region";
export const WRAPPER_CONFIG_COOKIE = "wrapperAppConfig";
export const GEO_RELOCATE_PROCESS_ID = "geoRelocateProcessId";

export const KEEP_LOGOUT_PATH_SESSION_STORAGE = "lkp";

export const isTvg5 = () => tvgConf().product === "tvg5";

const isFdrProduct = () =>
  PRODUCTS_FDR.some((product) => product === tvgConf().product);

export const decodeToken = (token?: string): AuthToken | {} => {
  try {
    if (token) {
      return jwt_decode(token);
    }
    return {};
  } catch (erro) {
    console.warn("Error trying decode jwt:", erro);
    return {};
  }
};

export const getUserSessionData = (authToken: string) => {
  const tokenDecoded = decodeToken(authToken);

  return {
    authToken,
    sessionId: get(tokenDecoded, "ses", ""),
    sessionExpireDate: get(tokenDecoded, "exp", ""),
    fdUserId: get(tokenDecoded, "sub", ""),
    userName: get(tokenDecoded, "usn", ""),
    userEmail: get(tokenDecoded, "eml", "")
  };
};

export const getUserSessionStartDate = (
  authToken?: string
): Date | undefined => {
  const tokenDecoded: AuthToken | {} = decodeToken(authToken);
  return authToken || authToken === "undefined"
    ? fromUnixTime(get(tokenDecoded, "crt", new Date()))
    : undefined;
};

export const createAuthTokenCookie = (token: string) => {
  addCookie(AUTH_TOKEN_COOKIE, token);
};

export const getAuthTokenCookie = () => getCookieValue(AUTH_TOKEN_COOKIE, "");

export const clearAuthTokenCookie = () => removeCookie(AUTH_TOKEN_COOKIE);

export const clearLoginTokenCookie = () => removeCookie(LOGIN_TOKEN_COOKIE);

export const getGeoTokenCookie = () => {
  let geoToken = "";
  if (typeof window !== "undefined") {
    geoToken = localStorage.getItem(GEO_TOKEN_COOKIE) || "";
  }

  return geoToken;
};

export const createGeoTokenCookie = (token: string) => {
  if (typeof window !== "undefined") {
    localStorage.setItem(GEO_TOKEN_COOKIE, token);
  }
};

export const setLogoutKeepPage = (keepCurrentPage: boolean) => {
  if (typeof window !== "undefined") {
    if (keepCurrentPage) {
      sessionStorage.setItem(
        KEEP_LOGOUT_PATH_SESSION_STORAGE,
        `${+keepCurrentPage}`
      );
    } else {
      sessionStorage.removeItem(KEEP_LOGOUT_PATH_SESSION_STORAGE);
    }
  }
};

export const getLogoutKeepPage = (): boolean => {
  if (typeof window !== "undefined") {
    return Boolean(sessionStorage.getItem(KEEP_LOGOUT_PATH_SESSION_STORAGE));
  }

  return false;
};

export const clearGeoTokenCookie = (removedBy?: string) => {
  if (typeof window !== "undefined") {
    if (removedBy) {
      amplitudeRemovedGeoToken(removedBy);
    }
    localStorage.removeItem(GEO_TOKEN_COOKIE);
  }
};

export const getLicenseCached = () => {
  let license = "";
  if (typeof window !== "undefined") {
    license = localStorage.getItem(GEO_LICENSE_COOKIE) || "";
  }

  return license;
};

export const createLicenseCached = (token: string) => {
  if (typeof window !== "undefined") {
    localStorage.setItem(GEO_LICENSE_COOKIE, token);
  }
};

export const clearLicenseCached = () => {
  if (typeof window !== "undefined") {
    localStorage.removeItem(GEO_LICENSE_COOKIE);
  }
};

export const getTemporaryRelocateUserProcessId = () => {
  let processId = "";
  if (typeof window !== "undefined") {
    processId = sessionStorage.getItem(GEO_RELOCATE_PROCESS_ID) || "";
  }

  return processId;
};

export const createTemporaryRelocateUserProcessId = (processId: string) => {
  if (typeof window !== "undefined") {
    sessionStorage.setItem(GEO_RELOCATE_PROCESS_ID, processId);
  }
};

export const clearTemporaryRelocateUserProcessId = () => {
  if (typeof window !== "undefined") {
    sessionStorage.removeItem(GEO_RELOCATE_PROCESS_ID);
  }
};

export const createSportsbookRegionCookie = (region: string) => {
  addCookie(SPORTSBOOK_REGION_COOKIE, region);
};

export const getSportsbookRegionCookie = () =>
  getCookieValue(SPORTSBOOK_REGION_COOKIE);

export const clearSportsbookRegionCookie = () => {
  removeCookie(SPORTSBOOK_REGION_COOKIE);
};

export const getLoginAuthToken = () => getCookieValue(LOGIN_TOKEN_COOKIE);

export const isSessionExpired = (authToken: string) => {
  const { sessionExpireDate } = getUserSessionData(authToken);
  return isAfter(new Date(), fromUnixTime(sessionExpireDate));
};

export const isAccountCompliantSelector = (store: Store): boolean => {
  const enableAWFlows = get(store, "capi.featureToggles.enableAWFlows", false);
  const logged = get(store, "userData.logged");
  const isVerified = get(store, "userData.isVerified");

  const requiresVerification = (enableAWFlows && isTvg5()) || isFdrProduct();

  if (logged && requiresVerification) {
    return !!isVerified;
  }

  return !!logged;
};

export const checkValidWageringState = (
  selectedState: string,
  allowedStates: Array<string>
): boolean =>
  !!selectedState && allowedStates && allowedStates.includes(selectedState);

export const createWrapperAppConfigCookie = (token: string) => {
  addCookie(WRAPPER_CONFIG_COOKIE, token);
};

export const removeWrapperAppConfigCookie = () => {
  removeCookie(WRAPPER_CONFIG_COOKIE);
};

export const getWrapperAppConfigCookie = () =>
  getCookieValue<string | undefined>(WRAPPER_CONFIG_COOKIE, undefined, false);
